// The rules a `help_request.types` list has to obey. They live beside the
// editor rather than inside it: ProjectConfigPage lifts the same rules to
// the save bar, and a module that exports both a component and a validator
// cannot be hot-replaced.

import {
  isReward,
  parseReward,
  rewardProblemText,
  type RewardProblem,
} from "../../../lib/reward";
import { SCHEMA_KEY_PATTERN } from "../../../components/MetaSchemaEditor/metaSchema";
import type { HelpRequestType } from "./types";
import { rangeError } from "./utils";

// Every bounded number on this page reports the same way, so the row's own
// message and the save bar's read as one voice — see rangeError.

/** What a fraction earns. The server reads every one of these keys with
 *  `positiveInt`, which takes a whole number or nothing at all: a saved `2.5`
 *  does not become 2, it makes the setting VANISH. The panel has to answer a
 *  fraction the same way the server does, or a game designer types a number,
 *  sees it accepted, and finds the field empty after a reload. */
const NOT_WHOLE = "Whole numbers only";

/** rangeError with the integer rule in front of it: `<input type="number">`
 *  hands a fraction straight through, so nothing else in the chain catches it
 *  before the config is written.
 *
 *  Exported because `giftTypes.ts` states the same three rules over its own
 *  keys. Two copies of "what is a number the server would act on" would be two
 *  places obliged to agree with the server AND with each other. */
export function wholeInRange(
  value: number,
  min: number,
  max: number,
): string | null {
  if (!Number.isFinite(value)) return rangeError(value, min, max);
  if (!Number.isInteger(value)) return NOT_WHOLE;
  return rangeError(value, min, max);
}

/** How the save bar names one of these problems, so the sentence stays true
 *  for both reasons a number can be refused. Shared with the gift list, whose
 *  save-bar sentences are built to the same shape. */
export function reasonFor(err: string): string {
  return err === NOT_WHOLE
    ? "that is not a whole number"
    : "outside the allowed range";
}

/** What the API accepts as a `request_type`. */
export const TYPE_REGEX = /^[a-z][a-z0-9_]*$/;

/** A hundred requests per window is already "unlimited" in any game sense;
 *  below one the type could never be asked for at all. */
export const MAX_MIN = 1;
export const MAX_MAX = 100;

/** A minute to thirty days — the same span `unclaimed_ttl_seconds` allows on
 *  this very section, so the two time fields cannot disagree on what a
 *  sane duration is. Under a minute is the rate limiter's job, not a quota's. */
export const WINDOW_MIN = 60;
export const WINDOW_MAX = 2592000;

/** What one request may gather in total. The ceiling is the column's: it is an
 *  INT, and a hundred thousand of anything is already "no cap" in a game
 *  economy. Below one the request would be fulfilled before the first
 *  donation. */
export const AMOUNT_MIN = 1;
export const AMOUNT_MAX = 100000;

/** How long a request stays open — and, since `ProjectConfigPage` bounds
 *  Unclaimed lifetime and Default TTL by these very constants, the span all
 *  THREE durations of the section obey. They cannot disagree about what a sane
 *  length of time is, because there is only one pair of numbers.
 *
 *  `0` is outside this span on purpose and is never checked against it: it is
 *  the ∞ mode, not a duration — see `ttlError` and `ttlFieldError`. */
export const TTL_MIN = 60;
export const TTL_MAX = 2592000;

/** What is wrong with a row's cap, if anything. A single key has no half-filled
 *  state, so out of range is the only way to get it wrong. */
export function amountError(row: HelpRequestType): string | undefined {
  return row.max_amount === undefined
    ? undefined
    : (wholeInRange(row.max_amount, AMOUNT_MIN, AMOUNT_MAX) ?? undefined);
}

/** What is wrong with a row's TTL. Two values are not wrong: empty means the
 *  section default applies, and `0` is the ∞ mode — "never expires", which the
 *  row's own toggle writes and the range has no business bounding. Both are
 *  configured states, not missing halves. */
export function ttlError(row: HelpRequestType): string | undefined {
  if (row.ttl_seconds === undefined || row.ttl_seconds === 0) return undefined;
  return wholeInRange(row.ttl_seconds, TTL_MIN, TTL_MAX) ?? undefined;
}

/**
 * What is wrong with a row's donor reward, if anything — the verdict, before
 * anybody words it.
 *
 * A reward the operator is still typing lives in the row AS THE TEXT THEY
 * TYPED: `donor_reward` holds the parsed value once that text is a reward, and
 * the raw string until then (see `HelpRequestType`). So a string here always
 * means an unfinished reward, and re-reading it says which kind — there is no
 * valid reward that is a string, since a scalar is not one.
 *
 * Every row that reaches here through the panel is already one of those two:
 * the normalizer admits nothing but an object or a list, and the editor writes
 * nothing but a value or the text. The last line is not for them — it is the
 * answer this function owes a field typed `unknown`, which is the honest type
 * for a value nobody reads. Unreachable today, and the day something does put
 * a scalar in a row, refusing it is what has to happen.
 */
export function donorRewardProblem(
  row: HelpRequestType,
): RewardProblem | undefined {
  const reward = row.donor_reward;
  // No reward is not a mistake — most types have none.
  if (reward === undefined) return undefined;
  if (typeof reward === "string") {
    // A string is a problem WHATEVER it says. Text that had become a reward
    // would have been stored as the value, so a string that parses into one is
    // a config nobody can have written here — and passing it would put a
    // quoted document on the wire, which the server reads as no reward at all.
    const parsed = parseReward(reward);
    return parsed.ok ? "not_a_container" : parsed.problem;
  }
  return isReward(reward) ? undefined : "not_a_container";
}

/** The row's own message under the reward box, in this field's own words. */
export function donorRewardError(row: HelpRequestType): string | undefined {
  const problem = donorRewardProblem(row);
  return problem === undefined
    ? undefined
    : rewardProblemText(problem, "Reward");
}

/** How the save bar names the same problem: it says which TYPE is broken, so
 *  the reason has to fit "…has a donor reward that is ___." */
function rewardReasonFor(problem: RewardProblem): string {
  return problem === "invalid_json"
    ? "not valid JSON"
    : "not an object or a list";
}

/** What is wrong with a row's quota. `pair` means half of it is filled in, so
 *  BOTH boxes are wrong and the reason is said once, under the window; `max`
 *  and `window` are a single box out of range, each speaking for itself. */
export interface LimitError {
  pair?: string;
  max?: string;
  window?: string;
}

/**
 * The quota rules, in one place: the editors paint boxes from this and the
 * list-level validators lift it to the save bar, so a row and the bar can never
 * disagree about what is broken.
 *
 * Written over two loose numbers rather than over a row, because a gift type
 * carries TWO of these pairs — one counting every recipient together, one
 * counting a single pair of players — and they obey the one rule.
 */
export function quotaError(
  max: number | undefined,
  seconds: number | undefined,
): LimitError | undefined {
  if (max !== undefined && seconds === undefined) return { pair: "Set a window" };
  if (seconds !== undefined && max === undefined) return { pair: "Set a max" };
  // Same wording as every other bounded field on this page, since it is the
  // same validator (`rangeError`).
  const maxRange =
    max === undefined ? null : wholeInRange(max, MAX_MIN, MAX_MAX);
  const windowRange =
    seconds === undefined
      ? null
      : wholeInRange(seconds, WINDOW_MIN, WINDOW_MAX);
  if (!maxRange && !windowRange) return undefined;
  return { max: maxRange ?? undefined, window: windowRange ?? undefined };
}

/** The quota of a request type — the pair above, read off the row's own keys. */
export function limitError(row: HelpRequestType): LimitError | undefined {
  return quotaError(row.max_per_window, row.window_seconds);
}

/**
 * Why this list cannot be SAVED, if it cannot. Mirrors `metaSchemaError`,
 * which lifts a row-level problem to the save bar.
 *
 * It blocks on what is structurally broken and nothing else: a nameless row
 * names no type at all, a duplicate loses one of its two rows the moment the
 * list is read as a map, and half a quota is a setting the server ignores
 * while the designer who typed it believes the type is limited. A number
 * outside its range blocks too — that is how every other bounded field on this
 * page behaves (`validateConfig` feeds them all through `rangeError`), and
 * unlike the naming rule below, no live project can already be holding one:
 * the keys ship with this change.
 *
 * A name that fails TYPE_REGEX is deliberately NOT blocking, even though the
 * row shows the warning. The list is now the allow-list the server checks, but
 * what it checks is string equality: a type named `"Cards"` works perfectly
 * well as long as the game sends `"Cards"`, so refusing to save it would be the
 * panel inventing a rule the platform does not have. And because this gate
 * disables Save for the WHOLE page, a live project that predates the regex
 * would find every other setting frozen behind a name it never had reason to
 * change — and, worse than before, could not fix its help configuration at all.
 */
/** What is wrong with a row's payload schema, if anything. The three ways a
 *  KEY can be wrong, and nothing about the values: a type's allow-list is the
 *  designer's own vocabulary, and there is nothing here that could judge it. */
export type PayloadSchemaProblem = "no_key" | "invalid_key" | "duplicate_key";

export function payloadSchemaProblem(
  row: HelpRequestType,
): PayloadSchemaProblem | undefined {
  const schema = row.payload_schema;
  // No schema is not a mistake — it is the state of every type of every
  // project, and it means "any payload".
  if (schema === undefined) return undefined;
  const seen = new Set<string>();
  for (const f of schema) {
    if (!f.key) return "no_key";
    if (!SCHEMA_KEY_PATTERN.test(f.key)) return "invalid_key";
    if (seen.has(f.key)) return "duplicate_key";
    seen.add(f.key);
  }
  return undefined;
}

/** What the collapsed schema header says in place of its field count when the
 *  schema is broken: short, because it stands where "3 fields" stood. */
export function payloadSchemaHeadError(
  problem: PayloadSchemaProblem,
): string {
  switch (problem) {
    case "no_key":
      return "Field with no key";
    case "invalid_key":
      return "Invalid key";
    case "duplicate_key":
      return "Duplicate key";
  }
}

/** How the save bar names the same problem: it says which TYPE is broken, so
 *  the reason has to fit "Payload schema of X has ___." */
function payloadSchemaReasonFor(problem: PayloadSchemaProblem): string {
  switch (problem) {
    case "no_key":
      return "a field with no key";
    case "invalid_key":
      return "an invalid key";
    case "duplicate_key":
      return "a duplicate key";
  }
}

export function helpTypesError(types: HelpRequestType[]): string | undefined {
  const seen = new Set<string>();
  for (const row of types) {
    if (!row.type) return "Every request type needs a name.";
    if (seen.has(row.type)) return `Duplicate request type “${row.type}”.`;
    seen.add(row.type);
    const limit = limitError(row);
    if (limit) {
      if (limit.pair) {
        return limit.pair === "Set a window"
          ? `Request type “${row.type}” has a max but no window.`
          : `Request type “${row.type}” has a window but no max.`;
      }
      const bad = limit.max ?? limit.window ?? "";
      return `Request type “${row.type}” has a quota ${reasonFor(bad)}.`;
    }
    // The cap and the TTL are single keys, so there is no half of one to be
    // missing: a number the server would not read is the only way to get them
    // wrong, said the same way the quota says it.
    const amount = amountError(row);
    if (amount) {
      return `Request type “${row.type}” has a max amount ${reasonFor(amount)}.`;
    }
    const ttl = ttlError(row);
    if (ttl) {
      return `Request type “${row.type}” has a ttl ${reasonFor(ttl)}.`;
    }
    // A reward that is not one would be SAVED as the operator's own text, and
    // the server would then hand out nothing while the row says it does. That
    // is the same "the designer believes it is set" the half quota above
    // blocks on.
    const reward = donorRewardProblem(row);
    if (reward) {
      return `Request type “${row.type}” has a donor reward that is ${rewardReasonFor(reward)}.`;
    }
    // A schema whose row is COLLAPSED still has to reach the save bar, or a
    // duplicate key hides behind a caret and the button just goes grey.
    const schema = payloadSchemaProblem(row);
    if (schema) {
      return `Payload schema of “${row.type}” has ${payloadSchemaReasonFor(schema)}.`;
    }
  }
  return undefined;
}
